// 셀프 입점 판정 한 벌 — 가입·조직 만들기·출시 알림 입력 해석과 RPC 인자 만들기(인메모리·Supabase 가 같이 쓴다).

import { isNameGiven, nameInvalidMessage, parseNameText, PERSON_NAME_MAX } from "../../lib/name-text";
import { kstMinute } from "../../lib/kst-time";
import { isPhoneFormat, PHONE_FORMAT_MESSAGE } from "../../lib/phone";
import { DEMO_SLUG } from "../admin/demo-org";
import { newPasswordIssue } from "../org/password";
import { isRejectedNote, reappliedNoteOf } from "../org/status";
import { COMPANY_NAME_RULE, type OrgSettingKey } from "../org/types";
import { FREE_PLAN_LIMITS } from "../plan/types";
import { defaultTemplateOf } from "../template/default-template";
import {
  EMAIL_MAX,
  MONTHLY_SITES,
  type MonthlySites,
  type OrgReapply,
  type OrgSelfCreate,
  PLAN_INTEREST_MEMO_MAX,
  PLAN_INTEREST_NAME_MAX,
  PLAN_INTEREST_NEEDS,
  type PlanInterestInsert,
  type PlanInterestNeed,
  REQUIRED_TERMS_DOCS,
  SIGNUP_SOURCES,
  type SignupSource,
  STAFF_SIZES,
  type StaffSize,
  TERMS_VERSION,
  WORK_TYPES,
  type WorkType,
} from "./types";

type Parsed<T> = { ok: true; value: T } | { ok: false; error: string };

// 한도 — 가입은 IP 당 시간 5번 · 조직 만들기는 계정당 하루 3번(설계 노트 36).
export const SIGNUP_PER_HOUR = 5;
export const SIGNUP_WINDOW_MS = 3_600_000;
export const ORG_CREATE_PER_DAY = 3;
export const ORG_CREATE_WINDOW_MS = 86_400_000;
export const PLAN_INTEREST_PER_MINUTE = 3;
// 같은 이메일로 나가는 메일(이미 가입 안내·확인 링크 다시·비밀번호 재설정) — 이메일당 시간 3통 · 화면 다시 보내기 대기(초)와 짝.
export const SIGNUP_MAIL_PER_HOUR = 3;
export const SIGNUP_RESEND_COOLDOWN_SECONDS = 60;

export const SIGNUP_RATE_MESSAGE = "가입 시도가 너무 많습니다. 1시간 뒤 다시 시도해 주세요";
export const ORG_CREATE_RATE_MESSAGE = "오늘은 시공사를 더 만들 수 없습니다. 내일 다시 시도해 주세요";
export const PLAN_INTEREST_RATE_MESSAGE = "신청을 너무 자주 보냈습니다. 1분 뒤 다시 보내 주세요";
export const ONBOARDING_UNAVAILABLE_MESSAGE = "지금은 처리할 수 없습니다. 잠시 뒤 다시 시도해 주세요";
export const SIGNUP_SENT_MESSAGE = "확인 메일을 보냈습니다. 메일의 링크를 눌러 비밀번호를 정해 주세요";
export const ORG_CREATED_MESSAGE = "시공사를 만들었습니다. 무료 요금제로 바로 시작할 수 있습니다";
export const ORG_PENDING_MESSAGE = "신청이 접수되었습니다 — 운영자 승인 후 사용하실 수 있습니다";
export const PLAN_INTEREST_SENT_MESSAGE = "출시 알림을 신청했습니다. 유료 요금제가 열리면 메일로 알려 드립니다";
export const SIGNUP_RESENT_MESSAGE = "확인 전인 가입 계정이면 확인 메일을 다시 보냈습니다. 메일함을 확인해 주세요";
// 비밀번호 재설정 메일 — 계정이 있든 없든·한도를 넘었든 같은 문구(가입 여부를 밖에 알리지 않는다).
export const PASSWORD_RESET_SENT_MESSAGE = "입력하신 주소로 계정이 있으면 비밀번호 재설정 메일을 보냈습니다. 메일함을 확인해 주세요";
export const PASSWORD_RESET_RATE_MESSAGE = "재설정 요청이 너무 많습니다. 1시간 뒤 다시 시도해 주세요";
export const TURNSTILE_FAILED_MESSAGE = "자동 가입 방지 확인에 실패했습니다. 화면을 새로 고친 뒤 다시 시도해 주세요";
// 확인 링크가 돌아오는 web 경로 — 복귀 주소 = BUILDPY_BASE_URL(없으면 요청 Origin) + 이 경로.
export const SIGNUP_VERIFY_PATH = "/signup/verify";
// web 로그인·재설정 경로 — 안내 메일·dev-auth·web 화면이 이 한 벌을 쓴다.
export const LOGIN_PATH = "/login";
// 로그인 뒤 돌아갈 주소(?next=) — 같은 출처 경로(/ 하나로 시작 · 역슬래시·제어문자 없음)만 받고 아니면 null(오픈 리다이렉트 방지).
export const safeNextPath = (raw: unknown): string | null => {
  if (typeof raw !== "string" || raw.length > 2000 || !raw.startsWith("/") || raw.startsWith("//")) return null;
  return /[\\\p{Cc}]/u.test(raw) ? null : raw;
};
// 로그인 화면 자신(/login·/login?…·/login#…) — next 로 따르면 되돌이표다.
export const isLoginPath = (path: string): boolean => /^\/login(?:[/?#]|$)/.test(path);
// 로그인 뒤 따를 next — 안전한 같은 출처 경로이고 로그인 화면 자신이 아닐 때만(아니면 null).
export const loginNextOf = (raw: unknown): string | null => {
  const safe = safeNextPath(raw);
  return safe === null || isLoginPath(safe) ? null : safe;
};
// 가드가 보낼 로그인 주소 — 안전한 next 가 있을 때만 붙인다(기본 화면 /app·로그인 화면 자신은 뺀다).
export const loginHref = (next?: unknown): string => {
  const safe = loginNextOf(next);
  return safe === null || safe === "/app" ? LOGIN_PATH : `${LOGIN_PATH}?next=${encodeURIComponent(safe)}`;
};
export const RESET_PASSWORD_PATH = "/reset-password";
export const FORGOT_PASSWORD_PATH = "/forgot-password";
export const SIGNUP_UNAVAILABLE_MESSAGE = "가입 준비 중입니다. 확인 메일을 보낼 수 있게 되면 다시 열립니다";
export const LOGIN_REQUIRED_MESSAGE = "이메일 확인을 마치고 로그인한 뒤 시공사를 만들 수 있습니다";
export const SELF_SIGNUP_REQUIRED_MESSAGE = "가입 화면에서 가입한 계정만 시공사를 만들 수 있습니다";
export const SIGNUP_PASSWORD_REQUIRED_MESSAGE = "비밀번호를 먼저 정해 주세요";
export const SIGNUP_PASSWORD_SET_MESSAGE = "비밀번호를 정했습니다";
export const SIGNUP_PASSWORD_DONE_MESSAGE = "이미 비밀번호를 정한 계정입니다. 로그인해 주세요";
// 로그인 화면 한 줄 — 셀프 가입은 확인 링크에서 비밀번호를 정한다.
export const LOGIN_SIGNUP_HINT = "가입 확인 메일의 링크를 눌러 비밀번호를 정해 주세요";

// bcrypt 가 72바이트 뒤를 버린다 — 넘으면 거절한다.
export const PASSWORD_MAX_BYTES = 72;
export { PERSON_NAME_MAX };
// 이름 입력 칸 maxLength — 브라우저는 UTF-16 단위로 세므로 2배(이모지만으로도 상한까지 들어가게) · 실제 상한은 검사가 코드포인트로 본다.
export const nameInputMax = (max: number): number => max * 2;
export const ADDRESS_MAX = 200;
// 가입 이름은 확인 메일 첫 줄에 실린다 — 줄바꿈·제어문자·주소처럼 보이는 글자로 남의 문구를 메일함에 넣지 못하게 한다.
export const SIGNUP_NAME_INVALID_MESSAGE = nameInvalidMessage("이름");
export const LICENSE_NO_MAX = 40;

const record = (raw: unknown): Record<string, unknown> =>
  raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};

const text = (raw: unknown): string => (typeof raw === "string" ? raw.trim() : "");

const personName = (raw: unknown, label: string, max = PERSON_NAME_MAX): Parsed<string> => parseNameText(raw, { label, max, url: true });

// 담당자(멤버) 이름 — 보이는 글자가 있으면 그 값 · 없으면 가입 때 적은 이름(검사를 지날 때만 — 설계 노트 36 「가입 이름 = member.name」) · 그것도 없으면 대표자명.
const ownerNameOf = (raw: unknown, signupName: string | undefined, ceo: Parsed<string>): Parsed<string> => {
  if (isNameGiven(raw, PERSON_NAME_MAX)) return personName(raw, "담당자 이름");
  const signup = personName(signupName, "이름");
  return signup.ok ? signup : ceo;
};

const oneOf = <T extends string>(list: readonly T[], raw: unknown): T | null =>
  typeof raw === "string" && (list as readonly string[]).includes(raw) ? (raw as T) : null;

// 소문자로 · 점이 있는 도메인만(링크를 전할 수 있어야 한다).
export const parseEmail = (raw: unknown): Parsed<string> => {
  const email = text(raw).toLowerCase();
  if (email === "") return { ok: false, error: "이메일을 넣어 주세요" };
  if (email.length > EMAIL_MAX || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return { ok: false, error: "이메일 형식이 올바르지 않습니다" };
  return { ok: true, value: email };
};

// 사업자등록번호 — 숫자 10자리(하이픈 허용) + 국세청 검증 자리.
export const isBizNo = (raw: string): boolean => {
  if (!/^\d{3}-?\d{2}-?\d{5}$/.test(raw)) return false;
  const d = raw.replace(/-/g, "").split("").map(Number);
  const weights = [1, 3, 7, 1, 3, 7, 1, 3, 5];
  let sum = weights.reduce((acc, w, i) => acc + w * (d[i] ?? 0), 0);
  sum += Math.floor(((d[8] ?? 0) * 5) / 10);
  return (10 - (sum % 10)) % 10 === d[9];
};

// 000-00-00000 로 맞춘다.
export const formatBizNo = (raw: string): string => {
  const digits = raw.replace(/-/g, "");
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

export interface SignupInput {
  email: string;
  name: string;
}

// 가입 폼 칸 — 화면이 오류 칸을 문구가 아니라 이 값으로 가른다.
export type SignupField = "name" | "email";
export type SignupParsed = { ok: true; value: SignupInput } | { ok: false; error: string; field: SignupField };

// 서버 검사와 화면 사전 검사가 이 한 벌을 쓴다.
export const parseSignupInput = (raw: unknown): SignupParsed => {
  const input = record(raw);
  const email = parseEmail(input.email);
  if (!email.ok) return { ...email, field: "email" };
  // 폭 없는 공백·채움 문자·홀로 쓴 이체자 선택자를 정리한 값으로 검사하고 저장한다.
  const name = personName(input.name, "이름");
  if (!name.ok) return { ...name, field: "name" };
  return { ok: true, value: { email: email.value, name: name.value } };
};

export interface SignupPasswordInput {
  password: string;
}

// 비밀번호 정하기 — 새 비밀번호 화면과 같은 규칙(org/password) + bcrypt 바이트 상한.
export const parseSignupPasswordInput = (raw: unknown): Parsed<SignupPasswordInput> => {
  const input = record(raw);
  const password = typeof input.password === "string" ? input.password : "";
  const confirm = typeof input.confirm === "string" ? input.confirm : "";
  const issue = newPasswordIssue(password, confirm);
  if (issue) return { ok: false, error: issue };
  if (new TextEncoder().encode(password).length > PASSWORD_MAX_BYTES) return { ok: false, error: "비밀번호가 너무 깁니다" };
  return { ok: true, value: { password } };
};

export interface OrgCreateInput {
  name: string;
  slug: string;
  tel: string;
  ceo: string;
  ownerName: string;
  ownerPhone: string | null;
  address: string;
  bizNo: string | null;
  licenseNo: string | null;
  staffSize: StaffSize | null;
  workTypes: WorkType[];
  monthlySites: MonthlySites | null;
  source: SignupSource | null;
}

export const ORG_CREATE_AGREE_MESSAGE = "이용약관에 동의하고 개인정보 처리방침을 확인해 주세요";

// 필수 — 이름·주소(slug)·전화·대표자·약관(terms·privacy) · 나머지는 선택(틀린 값은 거절, 빈 값은 null) · signupName 은 서버가 세션에서 읽은 가입 이름.
export const parseOrgCreateInput = (raw: unknown, signupName?: string): Parsed<OrgCreateInput> => {
  const input = record(raw);
  // 상호는 「OO닷컴」·도메인 꼴 상호가 있고 자기 고객·자기 대표에게만 보이므로 주소 꼴은 막지 않는다.
  const company = parseNameText(input.name, COMPANY_NAME_RULE);
  if (!company.ok) return company;
  const name = company.value;
  const slug = text(input.slug).toLowerCase();
  if (!DEMO_SLUG.test(slug)) return { ok: false, error: "주소는 소문자·숫자·하이픈 3~40자이고 하이픈으로 시작·끝나지 않습니다" };
  const tel = text(input.tel);
  if (!isPhoneFormat(tel)) return { ok: false, error: PHONE_FORMAT_MESSAGE };
  const ceoParsed = personName(input.ceo, "대표자 이름");
  if (!ceoParsed.ok) return ceoParsed;
  const ceo = ceoParsed.value;
  const owner = ownerNameOf(input.ownerName, signupName, ceoParsed);
  if (!owner.ok) return owner;
  const ownerName = owner.value;
  const ownerPhoneRaw = text(input.ownerPhone);
  if (ownerPhoneRaw !== "" && !isPhoneFormat(ownerPhoneRaw)) return { ok: false, error: PHONE_FORMAT_MESSAGE };
  const address = text(input.address);
  if (address.length > ADDRESS_MAX) return { ok: false, error: `주소는 ${ADDRESS_MAX}자까지입니다` };
  const bizNoRaw = text(input.bizNo);
  if (bizNoRaw !== "" && !isBizNo(bizNoRaw)) return { ok: false, error: "사업자등록번호가 올바르지 않습니다(000-00-00000)" };
  const licenseNo = text(input.licenseNo);
  if (licenseNo.length > LICENSE_NO_MAX) return { ok: false, error: `건설업 등록번호는 ${LICENSE_NO_MAX}자까지입니다` };
  const staffSize = oneOf(STAFF_SIZES, input.staffSize);
  if (input.staffSize != null && input.staffSize !== "" && !staffSize) return { ok: false, error: "직원 수를 목록에서 골라 주세요" };
  const rawTypes = Array.isArray(input.workTypes) ? input.workTypes : [];
  const workTypes = WORK_TYPES.filter((t) => rawTypes.includes(t));
  if (rawTypes.some((t) => !oneOf(WORK_TYPES, t))) return { ok: false, error: "공사 종류를 목록에서 골라 주세요" };
  const monthlySites = oneOf(MONTHLY_SITES, input.monthlySites);
  if (input.monthlySites != null && input.monthlySites !== "" && !monthlySites) return { ok: false, error: "월 현장 수를 목록에서 골라 주세요" };
  const source = oneOf(SIGNUP_SOURCES, input.source);
  if (input.source != null && input.source !== "" && !source) return { ok: false, error: "알게 된 경로를 목록에서 골라 주세요" };
  const agree = record(input.agree);
  if (REQUIRED_TERMS_DOCS.some((doc) => agree[doc] !== true)) return { ok: false, error: ORG_CREATE_AGREE_MESSAGE };
  return {
    ok: true,
    value: {
      name,
      slug,
      tel,
      ceo,
      ownerName,
      ownerPhone: ownerPhoneRaw || null,
      address,
      bizNo: bizNoRaw ? formatBizNo(bizNoRaw) : null,
      licenseNo: licenseNo || null,
      staffSize,
      workTypes,
      monthlySites,
      source,
    },
  };
};

// 셀프 입점이 쓰는 회사 설정 키 — 재신청이 비운 값은 이 키 안에서만 지운다.
export const COMPANY_SETTING_KEYS = ["company_name", "company_tel", "company_ceo", "company_address", "company_biz_no", "company_const_license_no"] as const satisfies readonly OrgSettingKey[];

// 회사 설정 — 빈 값은 쓰지 않는다(견적서·계약서가 설정에서 읽는다).
export const companySettingsOf = (input: OrgCreateInput): [OrgSettingKey, string][] =>
  (
    [
      ["company_name", input.name],
      ["company_tel", input.tel],
      ["company_ceo", input.ceo],
      ["company_address", input.address],
      ["company_biz_no", input.bizNo ?? ""],
      ["company_const_license_no", input.licenseNo ?? ""],
    ] as [OrgSettingKey, string][]
  ).filter(([, value]) => value !== "");

export const ORG_SELF_CREATED_ACTION = "org.self_created";

// RPC 인자 한 벌 — id 는 부른 쪽이 준다 · 무료 한도는 FREE_PLAN_LIMITS · 기록은 약관 동의·처리방침 확인 두 줄(마케팅은 받지 않는다) · 「기본」 견적 템플릿 하나(단가표는 비운다).
export const orgSelfCreateOf = (input: {
  userId: string;
  email: string;
  value: OrgCreateInput;
  newId: () => string;
  at: string;
  // 승인제(기본)면 승인 대기로 만든다 — 대기도 바뀐 시각이 있어야 한다(org_status_changed_at_check).
  approvalRequired: boolean;
}): OrgSelfCreate => {
  const { value, newId } = input;
  const status = input.approvalRequired ? { status: "pending" as const, status_changed_at: input.at } : { status: "active" as const, status_changed_at: null };
  return {
    userId: input.userId,
    org: { id: newId(), name: value.name, slug: value.slug, address: value.address, tel: value.tel, ...status },
    member: { id: newId(), name: value.ownerName, email: input.email, phone: value.ownerPhone },
    settings: companySettingsOf(value).map(([key, v]) => ({ id: newId(), key, value: v })),
    plan: { id: newId(), plan: "free", active_site_limit: null, ...FREE_PLAN_LIMITS },
    profile: {
      id: newId(),
      staff_size: value.staffSize,
      work_types: value.workTypes,
      monthly_sites: value.monthlySites,
      source: value.source,
    },
    consents: REQUIRED_TERMS_DOCS.map((doc) => ({ id: newId(), doc, version: TERMS_VERSION, agreed: true, agreed_at: input.at })),
    activity: {
      id: newId(),
      action: ORG_SELF_CREATED_ACTION,
      summary: `셀프 입점으로 시공사 「${value.name}」 을(를) 만들었습니다`,
      meta: { slug: value.slug, plan: "free", ...(value.source ? { source: value.source } : {}) },
    },
    template: defaultTemplateOf(newId()),
  };
};

// ── 가입 재신청 — 반려(해지 + 「가입 반려」 메모) 뒤 24시간이 지나면 대표가 회사 정보를 고쳐 다시 승인 대기로 ──
export const REAPPLY_WAIT_MS = 86_400_000;
export const ORG_REAPPLIED_MESSAGE = "다시 신청했습니다 — 운영자 승인 후 사용하실 수 있습니다";

// 반려 시각 + 24시간 · 시각이 없거나 못 읽으면 null.
export const reapplyAvailableAt = (changedAt: string | null | undefined): string | null => {
  const at = Date.parse(changedAt ?? "");
  return Number.isFinite(at) ? new Date(at + REAPPLY_WAIT_MS).toISOString() : null;
};

// 안내 시각은 분 올림 — 내림이면 안내한 분에 눌러도 아직 이르다(10:30:45 → 「10:31 이후」).
const ceilMinuteIso = (iso: string): string => {
  const at = Date.parse(iso);
  return Number.isFinite(at) ? new Date(Math.ceil(at / 60_000) * 60_000).toISOString() : iso;
};

export const reapplyTooEarlyMessage = (availableAt: string): string => `${kstMinute(ceilMinuteIso(availableAt))} 이후 다시 신청할 수 있습니다`;

export type ReapplyBlock = "forbidden" | "not_rejected" | "too_early";

// 재신청 판정 한 벌(0127 순서와 같다 — 대표 → 반려 → 24시간) · 막히면 그 이유, 되면 null.
export const reapplyBlockOf = (input: {
  role: string | null | undefined;
  org: { status: string | null; status_changed_at: string | null; status_note: string | null } | null;
  now: Date;
}): ReapplyBlock | null => {
  if (input.role !== "owner" || !input.org) return "forbidden";
  if (input.org.status !== "terminated" || !isRejectedNote(input.org.status_note)) return "not_rejected";
  const available = reapplyAvailableAt(input.org.status_changed_at);
  if (available === null || Date.parse(available) > input.now.getTime()) return "too_early";
  return null;
};

// 재신청 폼 초기값 — 입점 입력과 같은 꼴 · 설정 값이 있으면 그것(입점 뒤 설정에서 고쳤을 수 있다).
export const reapplyFormOf = (input: {
  org: { name: string; slug: string; address: string | null; tel: string | null };
  owner: { name: string; phone: string | null };
  settings: Partial<Record<OrgSettingKey, string | null>>;
  profile: Pick<OrgReapply["profile"], "staff_size" | "work_types" | "monthly_sites" | "source"> | null;
}): OrgCreateInput => ({
  name: input.settings.company_name || input.org.name,
  slug: input.org.slug,
  tel: input.settings.company_tel || input.org.tel || "",
  ceo: input.settings.company_ceo || "",
  ownerName: input.owner.name,
  ownerPhone: input.owner.phone,
  address: input.settings.company_address || input.org.address || "",
  bizNo: input.settings.company_biz_no || null,
  licenseNo: input.settings.company_const_license_no || null,
  staffSize: input.profile?.staff_size ?? null,
  workTypes: input.profile ? [...input.profile.work_types] : [],
  monthlySites: input.profile?.monthly_sites ?? null,
  source: input.profile?.source ?? null,
});

// 재신청 입력 — 셀프 입점 검사를 그대로 쓴다(주소는 지금 주소로 고정 · 약관은 입점 때 동의했다).
export const parseOrgReapplyInput = (raw: unknown, slug: string): Parsed<OrgCreateInput> =>
  parseOrgCreateInput({ ...record(raw), slug, agree: Object.fromEntries(REQUIRED_TERMS_DOCS.map((doc) => [doc, true])) });

// RPC 인자 한 벌 — 비운 회사 설정은 지우고 · 메모에 이전 반려 사유를 남긴다 · 승인제 스위치와 무관하게 승인 대기.
export const orgReapplyOf = (input: {
  orgId: string;
  userId: string;
  email: string;
  value: OrgCreateInput;
  prev: { status_changed_at: string | null; status_note: string | null };
  newId: () => string;
  at: string;
}): OrgReapply => {
  const { value, newId } = input;
  const settings = companySettingsOf(value);
  const filled = new Set(settings.map(([key]) => key));
  const note = reappliedNoteOf(input.prev.status_note);
  return {
    orgId: input.orgId,
    userId: input.userId,
    org: { name: value.name, address: value.address, tel: value.tel },
    member: { name: value.ownerName, phone: value.ownerPhone },
    settings: settings.map(([key, v]) => ({ id: newId(), key, value: v })),
    cleared_keys: COMPANY_SETTING_KEYS.filter((key) => !filled.has(key)),
    profile: { id: newId(), staff_size: value.staffSize, work_types: value.workTypes, monthly_sites: value.monthlySites, source: value.source },
    status_changed_at: input.at,
    status_note: note,
    audit_id: newId(),
    actor_email: input.email,
    audit_before: { status: "terminated", status_changed_at: input.prev.status_changed_at, status_note: input.prev.status_note },
    audit_after: { status: "pending", status_changed_at: input.at, status_note: note },
  };
};

export interface PlanInterestInput {
  needs: PlanInterestNeed[];
  memo: string | null;
  recipientName: string;
  email: string;
}

// 출시 알림 — 필요한 것 1개 이상 · 받는 사람 이름·이메일 필수 · 메모 선택.
export const parsePlanInterestInput = (raw: unknown): Parsed<PlanInterestInput> => {
  const input = record(raw);
  const rawNeeds = Array.isArray(input.needs) ? input.needs : [];
  if (rawNeeds.some((n) => !oneOf(PLAN_INTEREST_NEEDS, n))) return { ok: false, error: "필요한 것을 목록에서 골라 주세요" };
  const needs = PLAN_INTEREST_NEEDS.filter((n) => rawNeeds.includes(n));
  if (needs.length === 0) return { ok: false, error: "필요한 것을 하나 이상 골라 주세요" };
  const memo = text(input.memo);
  if (memo.length > PLAN_INTEREST_MEMO_MAX) return { ok: false, error: `메모는 ${PLAN_INTEREST_MEMO_MAX}자까지입니다` };
  // 받는 사람 이름 — 가입 이름과 같은 검사(정리한 값을 저장).
  const recipient = personName(input.recipientName ?? input.name, "받으실 분 이름", PLAN_INTEREST_NAME_MAX);
  if (!recipient.ok) return recipient;
  const recipientName = recipient.value;
  const email = parseEmail(input.email);
  if (!email.ok) return email;
  return { ok: true, value: { needs, memo: memo || null, recipientName, email: email.value } };
};

export const planInterestInsertOf = (input: { id: string; orgId: string; memberId: string; value: PlanInterestInput }): PlanInterestInsert => ({
  id: input.id,
  org_id: input.orgId,
  member_id: input.memberId,
  needs: input.value.needs,
  memo: input.value.memo,
  recipient_name: input.value.recipientName,
  email: input.value.email,
});
